/**
 * ============================================================================
 * NAVIGATION MENU COMPONENT
 * ============================================================================
 * 
 * Enhanced navigation menu with dropdowns, mobile support, and accessibility.
 * 
 * ============================================================================
 */



class NavigationMenu {
  constructor(element) {
    this.element = element;
    this.isMobile = window.innerWidth < 768;
    this.activeDropdown = null;
    this.mobileMenuOpen = false;
    
    this.init();
  }
  
  /**
   * Initialize navigation menu
   */
  init() {
    this.bindEvents();
    this.setupAccessibility();
    console.log('🧭 Navigation Menu initialized');
  }
  
  /**
   * Bind events
   */
  bindEvents() {
    // Mobile menu toggle
    on(this.element, 'click', (e) => {
      if (e.target.matches('[data-mobile-menu-toggle]')) {
        e.preventDefault();
        this.toggleMobileMenu();
      }
    });
    
    // Dropdown toggles
    on(this.element, 'click', (e) => {
      if (e.target.matches('[data-dropdown-toggle]')) {
        e.preventDefault();
        this.toggleDropdown(e.target);
      }
    });
    
    // Desktop hover events
    if (!this.isMobile) {
      this.setupDesktopHover();
    }
    
    // Keyboard navigation
    on(this.element, 'keydown', (e) => {
      this.handleKeyboardNavigation(e);
    });
    
    // Close dropdowns on outside click
    document.addEventListener('click', (e) => {
      if (!this.element.contains(e.target)) {
        this.closeAllDropdowns();
      }
    });
    
    // Handle window resize
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 768;
      
      if (wasMobile !== this.isMobile) {
        this.handleResponsiveChange();
      }
    });
    
    // Handle escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.mobileMenuOpen) {
          this.closeMobileMenu();
        } else {
          this.closeAllDropdowns();
        }
      }
    });
  }
  
  /**
   * Setup desktop hover events
   */
  setupDesktopHover() {
    const dropdownTriggers = $$('[data-dropdown-toggle]', this.element);
    
    dropdownTriggers.forEach(trigger => {
      const dropdown = this.getDropdownForTrigger(trigger);
      if (!dropdown) return;
      
      let hoverTimeout;
      
      // Mouse enter
      trigger.addEventListener('mouseenter', () => {
        clearTimeout(hoverTimeout);
        this.openDropdown(trigger);
      });
      
      // Mouse leave
      trigger.addEventListener('mouseleave', () => {
        hoverTimeout = setTimeout(() => {
          this.closeDropdown(trigger);
        }, 300);
      });
      
      // Keep dropdown open when hovering over it
      dropdown.addEventListener('mouseenter', () => {
        clearTimeout(hoverTimeout);
      });
      
      dropdown.addEventListener('mouseleave', () => {
        hoverTimeout = setTimeout(() => {
          this.closeDropdown(trigger);
        }, 300);
      });
    });
  }
  
  /**
   * Setup accessibility attributes
   */
  setupAccessibility() {
    const dropdownTriggers = $$('[data-dropdown-toggle]', this.element);
    
    dropdownTriggers.forEach(trigger => {
      const dropdown = this.getDropdownForTrigger(trigger);
      if (dropdown) {
        const dropdownId = dropdown.id || `dropdown-${Date.now()}`;
        dropdown.id = dropdownId;
        
        trigger.setAttribute('aria-haspopup', 'true');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-controls', dropdownId);
        
        dropdown.setAttribute('aria-hidden', 'true');
      }
    });
  }
  
  /**
   * Toggle mobile menu
   */
  toggleMobileMenu() {
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }
  
  /**
   * Open mobile menu
   */
  openMobileMenu() {
    this.mobileMenuOpen = true;
    addClass(this.element, 'mobile-menu-open');
    addClass(document.body, 'mobile-menu-open');
    
    // Update toggle button
    const toggle = $('[data-mobile-menu-toggle]', this.element);
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
    }
    
    // Emit event
    document.dispatchEvent(new CustomEvent('navigation:mobile:open', {
      detail: { menu: this.element }
    }));
  }
  
  /**
   * Close mobile menu
   */
  closeMobileMenu() {
    this.mobileMenuOpen = false;
    removeClass(this.element, 'mobile-menu-open');
    removeClass(document.body, 'mobile-menu-open');
    
    // Update toggle button
    const toggle = $('[data-mobile-menu-toggle]', this.element);
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
    }
    
    // Close all dropdowns
    this.closeAllDropdowns();
    
    // Emit event
    document.dispatchEvent(new CustomEvent('navigation:mobile:close', {
      detail: { menu: this.element }
    }));
  }
  
  /**
   * Toggle dropdown
   * @param {Element} trigger - Dropdown trigger element
   */
  toggleDropdown(trigger) {
    const dropdown = this.getDropdownForTrigger(trigger);
    if (!dropdown) return;
    
    const isOpen = hasClass(dropdown, 'show');
    
    if (isOpen) {
      this.closeDropdown(trigger);
    } else {
      this.openDropdown(trigger);
    }
  }
  
  /**
   * Open dropdown
   * @param {Element} trigger - Dropdown trigger element
   */
  openDropdown(trigger) {
    const dropdown = this.getDropdownForTrigger(trigger);
    if (!dropdown) return;
    
    // Close other dropdowns first
    this.closeAllDropdowns();
    
    // Open this dropdown
    addClass(dropdown, 'show');
    addClass(trigger, 'active');
    
    // Update accessibility
    trigger.setAttribute('aria-expanded', 'true');
    dropdown.setAttribute('aria-hidden', 'false');
    
    this.activeDropdown = { trigger, dropdown };
    
    // Emit event
    document.dispatchEvent(new CustomEvent('navigation:dropdown:open', {
      detail: { trigger, dropdown }
    }));
  }
  
  /**
   * Close dropdown
   * @param {Element} trigger - Dropdown trigger element
   */
  closeDropdown(trigger) {
    const dropdown = this.getDropdownForTrigger(trigger);
    if (!dropdown) return;
    
    removeClass(dropdown, 'show');
    removeClass(trigger, 'active');
    
    // Update accessibility
    trigger.setAttribute('aria-expanded', 'false');
    dropdown.setAttribute('aria-hidden', 'true');
    
    if (this.activeDropdown && this.activeDropdown.trigger === trigger) {
      this.activeDropdown = null;
    }
    
    // Emit event
    document.dispatchEvent(new CustomEvent('navigation:dropdown:close', {
      detail: { trigger, dropdown }
    }));
  }
  
  /**
   * Close all dropdowns
   */
  closeAllDropdowns() {
    const openDropdowns = $$('.dropdown.show', this.element);
    
    openDropdowns.forEach(dropdown => {
      const trigger = $(`[aria-controls="${dropdown.id}"]`, this.element);
      if (trigger) {
        this.closeDropdown(trigger);
      }
    });
  }
  
  /**
   * Get dropdown element for trigger
   * @param {Element} trigger - Trigger element
   * @returns {Element|null} Dropdown element
   */
  getDropdownForTrigger(trigger) {
    const dropdownId = trigger.getAttribute('aria-controls') || data(trigger, 'dropdown-target');
    return dropdownId ? $(`#${dropdownId}`) : trigger.nextElementSibling;
  }
  
  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} e - Keyboard event
   */
  handleKeyboardNavigation(e) {
    const { key } = e;
    const activeElement = document.activeElement;
    
    if (key === 'Enter' || key === ' ') {
      if (activeElement.matches('[data-dropdown-toggle]')) {
        e.preventDefault();
        this.toggleDropdown(activeElement);
      }
    }
    
    if (key === 'ArrowDown') {
      e.preventDefault();
      this.focusNextItem(activeElement);
    }
    
    if (key === 'ArrowUp') {
      e.preventDefault();
      this.focusPreviousItem(activeElement);
    }
  }
  
  /**
   * Focus next focusable item
   * @param {Element} currentElement - Current focused element
   */
  focusNextItem(currentElement) {
    const focusableElements = $$('a, button, [tabindex]:not([tabindex="-1"])', this.element);
    const currentIndex = Array.from(focusableElements).indexOf(currentElement);
    const nextIndex = (currentIndex + 1) % focusableElements.length;
    focusableElements[nextIndex].focus();
  }
  
  /**
   * Focus previous focusable item
   * @param {Element} currentElement - Current focused element
   */
  focusPreviousItem(currentElement) {
    const focusableElements = $$('a, button, [tabindex]:not([tabindex="-1"])', this.element);
    const currentIndex = Array.from(focusableElements).indexOf(currentElement);
    const previousIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
    focusableElements[previousIndex].focus();
  }
  
  /**
   * Handle responsive changes
   */
  handleResponsiveChange() {
    if (this.isMobile) {
      // Switched to mobile
      this.closeAllDropdowns();
    } else {
      // Switched to desktop
      this.closeMobileMenu();
      this.setupDesktopHover();
    }
  }
  
  /**
   * Destroy navigation menu component
   */
  destroy() {
    this.closeAllDropdowns();
    this.closeMobileMenu();
    
    // Remove event listeners
    off(this.element, 'click');
    off(this.element, 'keydown');
  }
}
